import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Loader2, AlertCircle, RefreshCw, Building2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantContext } from '../contexts/TenantContext';
import { useTenants } from '../hooks/useAdmin';

/**
 * TenantSwitcher — dropdown for Platform Admins to select the active tenant.
 * Renders in the Layout header. Only visible to PLATFORM_ADMIN role.
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.7, 9.8
 */
const TenantSwitcher: React.FC = () => {
  const { isPlatformAdmin } = useAuth();
  const { activeTenantName, setActiveTenant } = useTenantContext();
  const { data: tenants, loading, error, fetchTenants } = useTenants(50);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch tenants on first open
  const handleToggle = () => {
    if (!open && tenants.length === 0 && !loading) {
      fetchTenants();
    }
    setOpen((prev) => !prev);
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Guard: only render for Platform Admins
  if (!isPlatformAdmin) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={handleToggle}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className="w-4 h-4 text-gray-500" />
        <span className="max-w-[160px] truncate">
          {activeTenantName ?? 'No Tenant Selected'}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute right-0 mt-1 w-64 rounded-lg border border-gray-200 bg-white shadow-lg z-50"
          role="listbox"
          aria-label="Select a tenant"
        >
          {loading && (
            <div className="flex items-center justify-center py-4 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading tenants…
            </div>
          )}

          {error && !loading && (
            <div className="p-3 text-sm">
              <div className="flex items-center text-red-600 mb-2">
                <AlertCircle className="w-4 h-4 mr-1.5 flex-shrink-0" />
                <span className="truncate">{error}</span>
              </div>
              <button
                onClick={() => fetchTenants()}
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          )}

          {!loading && !error && tenants.length === 0 && (
            <div className="py-4 text-center text-sm text-gray-500">
              No tenants found
            </div>
          )}

          {!loading && !error && tenants.length > 0 && (
            <ul className="max-h-60 overflow-y-auto py-1">
              {tenants.map((tenant) => (
                <li key={tenant.tenantId}>
                  <button
                    onClick={() => {
                      setActiveTenant(tenant.tenantId, tenant.name);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                      activeTenantName === tenant.name
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    role="option"
                    aria-selected={activeTenantName === tenant.name}
                  >
                    {tenant.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
